import {
  AmenitiesShadeIcon,
  Button,
  EButtonColorVariant,
  ETextStyleVariant,
  GeneralManageVehiclesFillIcon,
  GeneralManageVehiclesOutlineIcon,
  RawLink,
  RideConvertibleIcon,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import Link from '@/components/switchback/Link/Link';
import { CAMPGROUND_SEARCH_PAGE } from '@/constants/urls';
import { useRideEntryPointImpression } from '@/hooks/useRideEntryPointImpression';
import { useShowRideLinks } from '@/hooks/useShowRideLinks';
import {
  ERideTransitionPlacement,
  trackRideTransitionSelectedEvent,
} from '@/services/analytics/ride';
import { getRideBaseUrl } from '@/utility/getRideBaseUrl';

// Bonfire's Ride icon set hardcodes fill="black" instead of currentColor —
// force the path to follow text color like the other tab icons.
const CarsTabIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <RideConvertibleIcon {...props} className="[&_path]:fill-current" />
);

export enum ETabsVariant {
  Rentals,
  Delivery,
  Campgrounds,
  Cars,
}

export enum ETabsColorVariant {
  White,
  Black,
}

export enum ETabsStyle {
  Buttons,
  Links,
}

interface ITabsProps {
  style?: ETabsStyle;
  tabVariant: ETabsVariant;
  colorVariant?: ETabsColorVariant;
  /** Supplied by the rendering surface so the header and each mobile tab row stay distinguishable */
  ridePlacement: ERideTransitionPlacement;
}

const RIDE_TAB_URL = `${getRideBaseUrl()}/?utm_source=outdoorsy&utm_medium=global_header`;

export const Tabs: React.FC<ITabsProps> = ({
  tabVariant,
  colorVariant = ETabsColorVariant.Black,
  style = ETabsStyle.Buttons,
  ridePlacement,
}) => {
  const intl = useIntl();
  const showRideLinks = useShowRideLinks();
  const rideTabImpressionRef = useRideEntryPointImpression(
    showRideLinks ? { placement: ridePlacement, destinationUrl: RIDE_TAB_URL } : null,
  );

  const tabs = [
    {
      label: intl.formatMessage({ defaultMessage: 'RV Rentals', id: 'ctb+gI' }),
      variant: ETabsVariant.Rentals,
      ActiveIcon: GeneralManageVehiclesFillIcon,
      InactiveIcon: GeneralManageVehiclesOutlineIcon,
      url: '/',
    },
    {
      label: intl.formatMessage({
        id: '19afVW',
        defaultMessage: 'Car Rentals',
        description: 'Global header tabs > Link to the ride.auto car rental marketplace',
      }),
      variant: ETabsVariant.Cars,
      ActiveIcon: CarsTabIcon,
      InactiveIcon: CarsTabIcon,
      url: RIDE_TAB_URL,
      onClick: () =>
        trackRideTransitionSelectedEvent({
          placement: ridePlacement,
          destinationUrl: RIDE_TAB_URL,
        }),
    },
    {
      label: intl.formatMessage({ defaultMessage: 'Campgrounds', id: '124tCD' }),
      variant: ETabsVariant.Campgrounds,
      ActiveIcon: AmenitiesShadeIcon,
      InactiveIcon: AmenitiesShadeIcon,
      url: CAMPGROUND_SEARCH_PAGE,
    },
    // The Cars tab is dropped below where Ride has no cars — it would otherwise
    // open an empty marketplace on a second brand.
  ].filter(tab => tab.variant !== ETabsVariant.Cars || showRideLinks);

  return (
    <div
      className={cn(
        'flex py-1 overflow-x-auto scrollbar-none',
        style === ETabsStyle.Links ? 'gap-4 px-1' : 'gap-1 md:gap-3',
      )}>
      {tabs.map(({ label, url, variant, ActiveIcon, InactiveIcon, onClick }, index) => {
        const isActive = variant === tabVariant;
        const IconComponent = isActive ? ActiveIcon : InactiveIcon;
        const ref = variant === ETabsVariant.Cars ? rideTabImpressionRef : undefined;
        return (
          <Link href={url} key={index} passHref forceClientsideRouting legacy>
            {style === ETabsStyle.Links ? (
              <RawLink
                ref={ref}
                href={url}
                onClick={onClick}
                aria-label={intl.formatMessage(
                  { defaultMessage: 'Navigate to {tabName}', id: 'w4XjBW' },
                  { tabName: label },
                )}
                className={
                  isActive
                    ? 'underline underline-offset-8 font-bold decoration-2'
                    : 'text-secondary-base'
                }>
                <Text
                  variant={ETextStyleVariant.MediumRegular}
                  className={isActive ? 'font-bold' : ''}>
                  {label}
                </Text>
              </RawLink>
            ) : (
              <Button
                ref={ref}
                label={
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-5 h-5 text-700" />
                    <Text variant={ETextStyleVariant.MediumBold} className="text-300 md:text-500">
                      {label}
                    </Text>
                  </div>
                }
                variant={isActive ? EButtonColorVariant.Tertiary : EButtonColorVariant.Secondary}
                onClick={onClick}
                className={cn('border whitespace-nowrap py-2 px-4', {
                  '!bg-gray-800': isActive,
                  '!bg-canvas-150': colorVariant === ETabsColorVariant.White && !isActive,
                })}
                aria-label={intl.formatMessage(
                  { defaultMessage: 'Navigate to {tabName}', id: 'w4XjBW' },
                  { tabName: label },
                )}
              />
            )}
          </Link>
        );
      })}
    </div>
  );
};
